import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatCurrencyEUR } from "@/lib/formatters";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PageHeader } from "../components/PageHeader";

const EMPTY_FORM = {
  name: "",
  duration_minutes: "",
  price: "",
  service_type: "",
};

const extractErrorMessage = (error, fallbackMessage) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail.trim();
  }
  if (typeof detail?.message === "string" && detail.message.trim()) {
    return detail.message.trim();
  }
  return fallbackMessage;
};

const normalizeServiceForm = (form) => ({
  name: form.name.trim(),
  duration_minutes: Number.parseInt(form.duration_minutes, 10),
  price: Number.parseFloat(form.price),
  service_type: form.service_type.trim(),
});

const validateServiceForm = (form) => {
  const normalized = normalizeServiceForm(form);

  if (!normalized.name) {
    return "Il nome del servizio è obbligatorio.";
  }
  if (!Number.isFinite(normalized.duration_minutes) || normalized.duration_minutes <= 0) {
    return "La durata deve essere maggiore di 0 minuti.";
  }
  if (normalized.duration_minutes > 480) {
    return "La durata non può superare 480 minuti.";
  }
  if (!Number.isFinite(normalized.price) || normalized.price < 0) {
    return "Il prezzo deve essere maggiore o uguale a 0.";
  }
  if (!normalized.service_type) {
    return "Il tipo di servizio è obbligatorio.";
  }

  return null;
};

const createEditFormFromService = (service) => ({
  name: service?.name ?? "",
  duration_minutes: String(service?.duration_minutes ?? ""),
  price: String(service?.price ?? ""),
  service_type: service?.service_type ?? "",
});

const buildServicePatchPayload = (service, form) => {
  const normalized = normalizeServiceForm(form);
  const payload = {};

  if (normalized.name !== (service?.name ?? "")) {
    payload.name = normalized.name;
  }
  if (normalized.duration_minutes !== service?.duration_minutes) {
    payload.duration_minutes = normalized.duration_minutes;
  }
  if (normalized.price !== service?.price) {
    payload.price = normalized.price;
  }
  if (normalized.service_type !== (service?.service_type ?? "")) {
    payload.service_type = normalized.service_type;
  }

  return payload;
};

export function ServicesPage() {
  const { isOwner } = useAuth();
  const [services, setServices] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingServiceId, setEditingServiceId] = useState("");
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [submittingCreate, setSubmittingCreate] = useState(false);
  const [savingServiceId, setSavingServiceId] = useState("");
  const [togglingServiceId, setTogglingServiceId] = useState("");
  const [expandedServiceIds, setExpandedServiceIds] = useState(() => new Set());

  const loadData = async () => {
    try {
      const response = await api.get("/services");
      setServices(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Caricamento servizi non riuscito."));
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const visibleServices = useMemo(() => {
    const filtered = isOwner
      ? services
      : services.filter((service) => service.active !== false);

    return [...filtered].sort((left, right) => {
      const leftActive = left.active !== false ? 1 : 0;
      const rightActive = right.active !== false ? 1 : 0;
      if (leftActive !== rightActive) {
        return rightActive - leftActive;
      }
      return (left.name ?? "").localeCompare(right.name ?? "", "it", {
        sensitivity: "base",
      });
    });
  }, [isOwner, services]);

  const handleChange = (event) => {
    setForm((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const handleEditChange = (event) => {
    setEditForm((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const submitService = async (event) => {
    event.preventDefault();
    const validationError = validateServiceForm(form);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setSubmittingCreate(true);
    try {
      await api.post("/services", normalizeServiceForm(form));
      toast.success("Servizio creato.");
      setForm(EMPTY_FORM);
      await loadData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Impossibile creare il servizio."));
    } finally {
      setSubmittingCreate(false);
    }
  };

  const startEdit = (service) => {
    setEditingServiceId(service.id);
    setEditForm(createEditFormFromService(service));
  };

  const cancelEdit = () => {
    setEditingServiceId("");
    setEditForm(EMPTY_FORM);
  };

  const submitEdit = async (service) => {
    const validationError = validateServiceForm(editForm);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const payload = buildServicePatchPayload(service, editForm);
    if (Object.keys(payload).length === 0) {
      toast.error("Nessuna modifica da salvare.");
      return;
    }

    setSavingServiceId(service.id);
    try {
      await api.patch(`/services/${service.id}`, payload);
      toast.success("Servizio aggiornato.");
      cancelEdit();
      await loadData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Impossibile aggiornare il servizio."));
    } finally {
      setSavingServiceId("");
    }
  };

  const toggleServiceActive = async (service) => {
    const nextActive = service.active === false;
    setTogglingServiceId(service.id);
    try {
      await api.patch(`/services/${service.id}`, { active: nextActive });
      toast.success(nextActive ? "Servizio riattivato." : "Servizio disattivato.");
      if (editingServiceId === service.id) {
        cancelEdit();
      }
      await loadData();
    } catch (error) {
      toast.error(
        extractErrorMessage(
          error,
          nextActive ? "Impossibile riattivare il servizio." : "Impossibile disattivare il servizio."
        )
      );
    } finally {
      setTogglingServiceId("");
    }
  };

  const toggleDescription = (serviceId) => {
    setExpandedServiceIds((current) => {
      const next = new Set(current);
      if (next.has(serviceId)) {
        next.delete(serviceId);
      } else {
        next.add(serviceId);
      }
      return next;
    });
  };

  return (
    <div className="space-y-6" data-testid="services-page">
      <PageHeader
        title="Servizi"
        subtitle="Gestisci i servizi prenotabili, le durate e i prezzi."
        titleTestId="services-title"
      />
      {isOwner && (
        <div className="min-w-0 bg-white border border-slate-200 rounded-sm p-4 sm:p-6" data-testid="services-form-card">
          <h2 className="text-lg font-semibold section-title" data-testid="services-form-title">
            Nuovo servizio
          </h2>
          <p className="mt-1 text-sm text-slate-500">Aggiungi un servizio prenotabile alla tua attività.</p>
          <form className="mt-4 grid md:grid-cols-2 gap-4" onSubmit={submitService} data-testid="services-form">
            <input
              name="name"
              aria-label="Nome servizio"
              value={form.name}
              onChange={handleChange}
              placeholder="Nome servizio"
              className="border border-slate-200 rounded-sm h-10 px-3"
              data-testid="service-name-input"
            />
            <input
              name="duration_minutes"
              type="number"
              aria-label="Durata servizio in minuti"
              min="1"
              max="480"
              value={form.duration_minutes}
              onChange={handleChange}
              placeholder="Durata (minuti)"
              className="border border-slate-200 rounded-sm h-10 px-3"
              data-testid="service-duration-input"
            />
            <input
              name="price"
              type="number"
              aria-label="Prezzo servizio"
              min="0"
              step="0.01"
              value={form.price}
              onChange={handleChange}
              placeholder="Prezzo"
              className="border border-slate-200 rounded-sm h-10 px-3"
              data-testid="service-price-input"
            />
            <input
              name="service_type"
              aria-label="Tipo servizio"
              value={form.service_type}
              onChange={handleChange}
              placeholder="Tipo servizio"
              className="border border-slate-200 rounded-sm h-10 px-3"
              data-testid="service-type-input"
            />
            <button
              type="submit"
              disabled={submittingCreate}
              className="bg-slate-900 text-white rounded-sm h-10 px-4 font-medium transition-transform active:scale-95 disabled:opacity-60"
              data-testid="service-submit-button"
            >
              {submittingCreate ? "Salvataggio..." : "Salva servizio"}
            </button>
          </form>
        </div>
      )}

      <div className="min-w-0 bg-white border border-slate-200 rounded-sm p-4 sm:p-6" data-testid="services-list-card">
        <h2 className="text-lg font-semibold section-title" data-testid="services-list-title">
          Servizi
        </h2>
        <p className="mt-1 text-sm text-slate-500">Consulta, modifica o disattiva i servizi disponibili.</p>
        <div className="mt-4 space-y-3" data-testid="services-list">
          {visibleServices.map((service) => {
            const isEditing = editingServiceId === service.id;
            const isInactive = service.active === false;
            const isSaving = savingServiceId === service.id;
            const isToggling = togglingServiceId === service.id;
            const description = service.description || service.service_type || "";
            const isDescriptionExpanded = expandedServiceIds.has(service.id);
            const canExpandDescription = description.length > 72;

            return (
              <div
                key={service.id}
                className={`border rounded-sm p-3 ${isInactive ? "border-slate-300 bg-slate-50 text-slate-500" : "border-slate-200 bg-white"}`}
                data-testid={`service-${service.id}`}
              >
                <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="min-w-0 break-words text-sm font-medium" data-testid={`service-name-${service.id}`}>
                        {service.name}
                      </p>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          isInactive ? "bg-slate-200 text-slate-600" : "bg-emerald-100 text-emerald-700"
                        }`}
                      >
                        {isInactive ? "Disattivato" : "Attivo"}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500" data-testid={`service-details-${service.id}`}>
                      {service.duration_minutes} min · {formatCurrencyEUR(service.price)}
                    </p>
                    {description && (
                      <div className="mt-2 min-w-0">
                        <p
                          className={`break-words text-xs leading-5 text-slate-500 ${
                            !isDescriptionExpanded && canExpandDescription ? "service-description--collapsed" : ""
                          }`}
                          data-testid={`service-description-${service.id}`}
                        >
                          {description}
                        </p>
                        {canExpandDescription && (
                          <button
                            type="button"
                            className="mt-1 min-h-9 text-xs font-medium text-slate-700 underline underline-offset-2"
                            onClick={() => toggleDescription(service.id)}
                            aria-expanded={isDescriptionExpanded}
                            data-testid={`service-description-toggle-${service.id}`}
                          >
                            {isDescriptionExpanded ? "Mostra meno" : "Mostra altro"}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {isOwner && !isEditing && (
                    <div className="flex w-full min-w-0 flex-col gap-2 sm:w-auto sm:shrink-0 sm:flex-row sm:justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => startEdit(service)}
                        className="min-h-11 w-full whitespace-normal sm:min-h-9 sm:w-auto"
                        data-testid={`service-edit-${service.id}`}
                      >
                        Modifica
                      </Button>
                      <Button
                        type="button"
                        variant={isInactive ? "outline" : "destructive"}
                        onClick={() => toggleServiceActive(service)}
                        disabled={isToggling}
                        className="min-h-11 w-full whitespace-normal sm:min-h-9 sm:w-auto"
                        data-testid={`service-toggle-active-${service.id}`}
                      >
                        {isToggling ? "Aggiornamento..." : isInactive ? "Riattiva" : "Disattiva"}
                      </Button>
                    </div>
                  )}
                </div>

                {isOwner && isEditing && (
                  <form
                    className="mt-4 grid gap-3 md:grid-cols-2"
                    onSubmit={(event) => {
                      event.preventDefault();
                      submitEdit(service);
                    }}
                    data-testid={`service-edit-form-${service.id}`}
                  >
                    <input
                      name="name"
                      value={editForm.name}
                      onChange={handleEditChange}
                      placeholder="Nome servizio"
                      className="border border-slate-200 rounded-sm h-10 px-3"
                    />
                    <input
                      name="duration_minutes"
                      type="number"
                      min="1"
                      max="480"
                      value={editForm.duration_minutes}
                      onChange={handleEditChange}
                      placeholder="Durata (minuti)"
                      className="border border-slate-200 rounded-sm h-10 px-3"
                    />
                    <input
                      name="price"
                      type="number"
                      min="0"
                      step="0.01"
                      value={editForm.price}
                      onChange={handleEditChange}
                      placeholder="Prezzo"
                      className="border border-slate-200 rounded-sm h-10 px-3"
                    />
                    <input
                      name="service_type"
                      value={editForm.service_type}
                      onChange={handleEditChange}
                      placeholder="Tipo servizio"
                      className="border border-slate-200 rounded-sm h-10 px-3"
                    />
                    <div className="flex flex-wrap gap-2 md:col-span-2">
                      <button
                        type="submit"
                        disabled={isSaving}
                        className="bg-slate-900 text-white rounded-sm h-10 px-4 text-sm font-medium disabled:opacity-60"
                        data-testid={`service-save-${service.id}`}
                      >
                        {isSaving ? "Salvataggio..." : "Salva"}
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="border border-slate-200 rounded-sm h-10 px-4 text-sm font-medium"
                        data-testid={`service-cancel-edit-${service.id}`}
                      >
                        Annulla
                      </button>
                    </div>
                  </form>
                )}
              </div>
            );
          })}
          {visibleServices.length === 0 && (
            <div className="border border-dashed border-slate-200 rounded-sm p-4 text-sm text-slate-500">
              Nessun servizio disponibile.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
